import { STEP_ITEM_HEIGHT } from "~/components/build/Show/routes/list/-components/constants";
import { TreeNode } from "~/components/build/Show/routes/list/-components/TreeNode";
import { JobLabel } from "~/components/build/Show/routes/list/-components/TreeNode/JobLabel";
import { StepLink } from "~/components/build/Show/routes/list/-components/TreeNode/StepLink";
import { CommandJob } from "~/components/build/Show/lib/types";
import { isJobIgnored } from "~/components/build/Show/lib/isJobIgnored";
import { resolveJobUrl } from "~/components/build/Show/lib/urlForView";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { CommandStep, State } from "~/lib/pipeline";

interface Props {
  step: CommandStep;
  job: CommandJob;
  /** 1-based position of this attempt within the retry chain. */
  attempt: number;
  depth: number;
}

/**
 * A single past retry attempt. Renders as the same job row as the latest
 * attempt it nests under (state icon, name, queue, timing), distinguished only
 * by an "Attempt #N" badge. Clicking opens that attempt's job in the drawer.
 *
 * Unlike regular rows, attempts are intentionally not failure/running
 * highlighted — past attempts are usually failed, so highlighting them all
 * would be noise. The attention-drawing highlight stays on the parent row.
 */
export function JobRetryItem({ step, job, attempt, depth }: Props) {
  const params = useQueryParams();

  return (
    <StepLink
      state={isJobIgnored(job) ? State.Ignored : step.state}
      type={step.type}
      to={resolveJobUrl(job.id, { open: !isJobIgnored(job) })}
      active={job.id === params.jid}
      depth={depth}
    >
      <JobLabel job={job} step={step} depth={depth} attempt={attempt} />
    </StepLink>
  );
}

export const createJobRetryNode = (
  step: CommandStep,
  job: CommandJob,
  attempt: number,
): TreeNode => ({
  id: job.id,
  height: STEP_ITEM_HEIGHT,
  render: ({ depth }) => <JobRetryItem step={step} job={job} attempt={attempt} depth={depth} />,
});
